import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { checkApi } from '../../Authentication/AxiosInstance';
import { DEFAULT_ACADEMIC_YEAR } from '../../../config/constants';
import { useNavigate } from 'react-router-dom';

const SDExamSchedules = ({ 
  candidateId,
  refreshExamList,
  setRefreshExamList,
}) => {
  const navigate = useNavigate();
  const [candidateExams, setCandidateExams] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true); // Profile section expanded by default
   
  //Populate candidateExams with candidate-exams data
  const fetchCandidateExams = async () => {
    if (!candidateId) return;
    try {
      setLoading(true);
      // Use the my_exams endpoint that includes phase information
      const response = await checkApi.post(`/candidates/candidate-exams/my_exams/`, {
        cndt_academicyear: DEFAULT_ACADEMIC_YEAR
      });
      setCandidateExams(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching candidate exams:', error);
      setError(error.response?.data?.error || 'Failed to fetch candidate exams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  console.log('candidateExams:', candidateExams);
  
  
  // Exam Tracker Card Start
  function ExamTrackerCard({ exam }) {
    const completed_activity = exam.phase_info?.completed_phase;
    const current_activity = exam.phase_info?.current_phase;
    const next_activity = exam.phase_info?.next_phase;
    return (
      <div className="rounded-xl shadow bg-white h-auto">
          {/* Header (compact) */}
          <div className="px-4 py-2 border-b border-purple-500 rounded-t-xl">
            <div className="bg-white flex items-center justify-between">
              <h3 className="text-purple-500 font-semibold text-sm truncate flex items-center gap-2">
                <span>{exam.exam_name?.toUpperCase()}</span>
                <span>-</span>
                <span>{exam.exam_session?.toUpperCase()}</span>
              </h3>
            </div>
          </div>
          {/* Activity Body (compact) */}
          {completed_activity && (
              <div className="rounded-lg bg-gray-200 text-gray-600 m-2 p-2">
                <div className="font-semibold">(Completed)</div>
                <div className="text-lg font-semibold"> {completed_activity.phase}</div>
                {(completed_activity.start === completed_activity.end ) ? (
                  <div className="text-sm opacity-90">{completed_activity.start} </div>
                ) : (
                  <div className="text-sm opacity-90">{completed_activity.start} to {completed_activity.end}</div>
                )}
              </div>
          )}
          {/* Current Activity */}
          {current_activity && (
            <div className="rounded-lg bg-[#4CAF50] text-white m-2 p-2">
                <div className="font-semibold">(Ongoing)</div>
                <div className="text-lg font-semibold"> {current_activity.phase}</div>
                {current_activity.start === current_activity.end 
                  ? (<div className="text-sm opacity-90">{current_activity.start} </div>) 
                  : (<div className="text-sm opacity-90">{current_activity.start} to {current_activity.end}</div>)
                }
                {current_activity.remarks && <div className="text-xs opacity-75 mt-1">{current_activity.remarks}</div>}
            </div>
          )}

          {/* Next Activity */}
          {next_activity && (
            <div className="rounded-lg bg-yellow-100 text-amber-900 m-2 p-2">
                <div className="font-semibold">(Next)</div> 
                <div className="text-lg font-semibold"> {next_activity.phase}</div>
                { next_activity.start === next_activity.end 
                  ? <div className="text-sm opacity-90">{next_activity.start} </div>
                  : <div className="text-sm opacity-90">{next_activity.start} to {next_activity.end}</div>
                }
                {next_activity.remarks && <div className="text-sm opacity-75 mt-1">{next_activity.remarks}</div>}
          </div>
          )}

          {/* Fallback if no activities */}
          {!current_activity && !next_activity && !completed_activity && (
            <div className="rounded-lg px-3 py-2 bg-gray-100 text-gray-600 m-2 p-2">
              <div className="font-semibold">Schedule not available</div>
              <div className="text-xs">Check back later for updates</div>
          </div>
          )}
        </div>
      )  
  }
  // Exam Tracker Card End

// Carousel Functions Start
const [activeIdx, setActiveIdx] = useState(0);
// Responsive config
const getConfig = () => {
  if (window.innerWidth >= 1024) return { cols: 3, visible: 3 };
  return { cols: 1, visible: 1 };
};
const cr_config = getConfig();
const maxIdx = Math.max(0, Math.ceil(candidateExams.length / cr_config.visible) - 1);
// Touch handlers
const touch = useRef({ startX: 0, dx: 0 });
const onTouchStart = (e) => {
  touch.current.startX = e.touches[0].clientX;
};
const onTouchMove = (e) => {
  touch.current.dx = e.touches[0].clientX - touch.current.startX;
};
const onTouchEnd = () => {
  const threshold = 60;
  if (touch.current.dx < -threshold && activeIdx < maxIdx) {
    setActiveIdx(prev => Math.min(prev + 1, maxIdx));
  } else if (touch.current.dx > threshold && activeIdx > 0) {
    setActiveIdx(prev => Math.max(prev - 1, 0));
  }
};
// Use current-year exams for carousel calculations
const numSlidesCY = Math.max(1, Math.ceil(candidateExams.length / cr_config.visible));
// Carousel Functions End

useEffect(() => {
      fetchCandidateExams();
      setRefreshExamList(false);
}, []);

console.log(candidateExams);

return (
  <div className="rounded-lg shadow-md shadow-purple-200">
      <div className="bg-tertiary-600 rounded-xl">


      {/* Header */}
      <div className="flex items-center justify-between shadow-md shadow-purple-200 p-2 rounded-lg">
          <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                  {/* Stage and title */}
                  <div className="h-8 w-8 bg-purple-400 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                        {'My Exam Schedules'} ({DEFAULT_ACADEMIC_YEAR})
                  </h3>
              </div>
          </div>
          {/* View All and Collapse/Expand Button */}
          <div className="flex items-center space-x-2">
              <button onClick={() => navigate('/cetzone/exam-dates/')}  
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-400 
                          text-white rounded-lg hover:bg-purple-800 transition-colors h-10"
              >
                <EyeIcon className="w-4 h-4" />
                <span>View All</span>
              </button>
              {/* Collapse/Expand Button */}
              <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center justify-center w-10 h-10 bg-purple-400 font-semibold text-white rounded-lg hover:bg-purple-800 transition-colors"
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5" />
                    ) : (
                        <ChevronDownIcon className="w-5 h-5" />
                    )}
              </button>
          </div>
        </div>
      {/* Loading */}
      {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div> 
              <p className="text-gray-600 mt-2">Loading exams...</p>
            </div>
      )}
      {/* Success/Error Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      {/* Exam List */}
      {isExpanded && (
        <div className="space-y-4">
            { candidateExams.length === 0 ? (
              <div className="flex items-center space-x-3">
                <h2 className="text-base font-normal text-gray-900">Please add an exam to your profile</h2>
              </div>
            ) : (
              <div className = 'mt-4 p-2'>
                {/* Carousel Container */}
                  <div 
                      className="relative overflow-hidden"
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={onTouchEnd}
                    >
                      {/* Exam Tracker Cards Container */}
                      <div
                        className="flex transition-transform duration-300 ease-out"
                        style={{ 
                          transform: `translateX(-${activeIdx * 100}%)`
                        }}
                      >
                        {/* Exam Tracker Cards Display */}
                        {Array.from({ length: Math.ceil(candidateExams.length / cr_config.visible) }).map((_, batchIdx) => (
                              <div key={batchIdx} className="w-full shrink-0">
                                <div className={`grid gap-4 ${cr_config.cols === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
                                  {candidateExams
                                    .slice(batchIdx * cr_config.visible, (batchIdx + 1) * cr_config.visible)
                                    .map((exam) => { 
                                        return <ExamTrackerCard
                                        key={exam.id}
                                        exam={exam}
                                        className="shrink-0 w-[32%] px-2" 
                                  />
                                })}
                              </div>
                            </div>
                        ))}
                      </div>
                      {/* Navigation Dots */}
                      <div className="mt-3 flex justify-center gap-2">
                        {Array.from({ length: numSlidesCY }).map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveIdx(i)}
                            className={`h-2 w-2 rounded-full ${i === activeIdx ? 'bg-gray-800' : 'bg-gray-300'}`}
                            aria-label={`Go to slide ${i + 1}`}
                          />
                        ))}
                      </div>
                  </div>
                  <button disabled={activeIdx===0} onClick={() => setActiveIdx(i=>i-1)} 
                                className="absolute left-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-white/80 rounded">‹</button>
                  <button disabled={activeIdx===numSlidesCY-1} onClick={() => setActiveIdx(i=>i+1)} 
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-white/80 rounded">›</button>
              </div>
            )}
        </div>
      )}
    </div>
  </div>
  );
};

export default SDExamSchedules;